/**
 * Tenant API Transformers
 * Maps between API DTOs and Domain models (Anti-Corruption Layer)
 */

import type { Tenant, TenantsFilter, TenantStats } from '../domain/types/tenant.types'
import type {
  TenantDto,
  CreateTenantRequest,
  UpdateTenantRequest,
  TenantStatsResponse,
} from './api-types'

// ===========================================
// API -> Domain Transformers
// ===========================================

export function transformDtoToTenant(dto: TenantDto): Tenant {
  return {
    id: String(dto.id),
    documentId: dto.documentId,
    code: dto.code,
    name: dto.name,
    legalName: dto.legalName ?? undefined,
    taxId: dto.taxId ?? undefined,
    vatNumber: dto.vatNumber ?? undefined,
    registrationNumber: dto.registrationNumber ?? undefined,
    status: dto.tenantStatus,
    channel: dto.channel,
    customerGroup: dto.customerGroup ? {
      id: String(dto.customerGroup.id),
      name: dto.customerGroup.name,
      code: dto.customerGroup.code,
    } : undefined,
    usersCount: typeof dto.usersCount === 'number' ? dto.usersCount : undefined,
    lastSalesOrderCreatedAt: dto.lastSalesOrderCreatedAt ?? undefined,
    // Address fields
    addressLine1: dto.addressLine1 ?? undefined,
    houseNumber: dto.houseNumber ?? undefined,
    city: dto.city ?? undefined,
    region: dto.region ?? undefined,
    postalCode: dto.postalCode ?? undefined,
    telephone: dto.telephone ?? undefined,
    country: dto.country ?? undefined,
    sapCustomerGroup: dto.sapCustomerGroup ?? undefined,
    sapBusinessPartnerNumber: dto.sapBusinessPartnerNumber ?? undefined,
    // Documents
    attachments: Array.isArray(dto.attachments)
      ? dto.attachments.map(a => ({ name: a.name, link: a.link }))
      : undefined,
    createdAt: dto.createdAt,
    updatedAt: dto.updatedAt,
  }
}

export function transformDtosToTenants(dtos: TenantDto[]): Tenant[] {
  return dtos.map(transformDtoToTenant)
}

export function transformStatsResponseToStats(response: TenantStatsResponse): TenantStats {
  return {
    total: response.total,
    pending: response.pending,
    active: response.active,
    suspended: response.suspended,
  }
}

// ===========================================
// Domain -> API Transformers
// ===========================================

/**
 * Transform domain filter to API query params (Strapi format)
 */
export function transformFilterToQuery(filter: TenantsFilter): Record<string, unknown> {
  const params: Record<string, unknown> = {
    'populate[customerGroup]': true,
    'populate[attachments]': true,
    'sort': 'createdAt:desc',
  }

  // Pagination (Strapi format)
  if (filter.page) {
    params['pagination[page]'] = filter.page
  }
  if (filter.pageSize) {
    params['pagination[pageSize]'] = filter.pageSize
  }

  // Filters
  if (filter.status && filter.status !== 'all') {
    params['filters[tenantStatus][$eq]'] = filter.status
  }
  if (filter.channel && filter.channel !== 'all') {
    params['filters[channel][$eq]'] = filter.channel
  }
  if (filter.search) {
    params['filters[$or][0][name][$containsi]'] = filter.search
    params['filters[$or][1][code][$containsi]'] = filter.search
    params['filters[$or][2][legalName][$containsi]'] = filter.search
    params['filters[$or][3][sapBusinessPartnerNumber][$containsi]'] = filter.search
  }

  return params
}

export function transformToCreateRequest(
  data: Omit<Tenant, 'id' | 'documentId' | 'createdAt' | 'updatedAt'> & { customerGroupId?: number }
): CreateTenantRequest {
  return {
    code: data.code,
    name: data.name,
    legalName: data.legalName,
    taxId: data.taxId,
    vatNumber: data.vatNumber,
    registrationNumber: data.registrationNumber,
    channel: data.channel,
    customerGroupId: data.customerGroupId,
  }
}

export function transformToUpdateRequest(
  data: Partial<Pick<Tenant, 'code' | 'name' | 'legalName' | 'taxId' | 'vatNumber' | 'registrationNumber' | 'channel' | 'status'>> & {
    customerGroupId?: number
  }
): UpdateTenantRequest {
  return {
    code: data.code ? data.code.trim().toUpperCase() : undefined,
    name: data.name,
    legalName: data.legalName,
    taxId: data.taxId,
    vatNumber: data.vatNumber,
    registrationNumber: data.registrationNumber,
    channel: data.channel,
    customerGroupId: data.customerGroupId,
    tenantStatus: data.status,
  }
}
